import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { appointmentAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatShortDate, getStatusLabel, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { format, addDays, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Scissors,
  Calendar,
  Clock,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Check,
  X
} from 'lucide-react';

export default function BarberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    loadAppointments();
  }, [selectedDate]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await appointmentAPI.list({ date: dateStr });
      setAppointments(response.data);
    } catch (error) {
      console.error('Errore caricamento appuntamenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentAPI.updateStatus(appointmentId, newStatus);
      toast.success('Stato aggiornato');
      loadAppointments();
    } catch (error) {
      toast.error('Errore aggiornamento stato');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logout effettuato');
  };

  const goToDay = (days) => {
    setSelectedDate(prev => addDays(prev, days));
  };

  const todayAppointments = appointments.filter(apt => apt.status !== 'ANNULLATO');
  const completedToday = appointments.filter(apt => apt.status === 'COMPLETATO').length;

  // Generate week days
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <Scissors className="w-6 h-6 text-primary" />
            <span className="font-semibold">AppLook - Operatore</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.name}
            </span>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Dashboard Operatore</h1>
            <p className="text-muted-foreground">
              Gestisci i tuoi appuntamenti del giorno
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{todayAppointments.length}</p>
                    <p className="text-sm text-muted-foreground">Oggi</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{completedToday}</p>
                    <p className="text-sm text-muted-foreground">Completati</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {todayAppointments.filter(a => a.status === 'IN_ATTESA').length}
                    </p>
                    <p className="text-sm text-muted-foreground">In Attesa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Date Navigation */}
          <Card className="mb-8">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="icon" onClick={() => goToDay(-7)} data-testid="prev-week">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="font-semibold">
                  {format(selectedDate, 'MMMM yyyy', { locale: it })}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => goToDay(7)} data-testid="next-week">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((day) => {
                  const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        isSelected 
                          ? 'bg-primary text-primary-foreground' 
                          : isToday 
                            ? 'bg-primary/10' 
                            : 'hover:bg-muted'
                      }`}
                      data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <p className="text-xs uppercase mb-1">
                        {format(day, 'EEE', { locale: it })}
                      </p>
                      <p className="font-semibold">{format(day, 'd')}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Appointments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Appuntamenti - {format(selectedDate, 'EEEE d MMMM', { locale: it })}</span>
                <Button variant="outline" size="sm" onClick={loadAppointments}>
                  Aggiorna
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : todayAppointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Nessun appuntamento per questa data
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {todayAppointments
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((apt) => (
                    <div 
                      key={apt.id} 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4"
                      data-testid={`barber-apt-${apt.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 text-center">
                          <p className="text-2xl font-bold">{apt.time}</p>
                          <p className="text-xs text-muted-foreground">
                            {apt.duration_minutes} min
                          </p>
                        </div>
                        <div>
                          <h3 className="font-semibold">{apt.service?.name || 'Servizio'}</h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {apt.user_id 
                              ? 'Cliente registrato' 
                              : `${apt.guest_name} - ${apt.guest_phone}`
                            }
                          </p>
                          {apt.notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Note: {apt.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Select
                          value={apt.status}
                          onValueChange={(value) => handleStatusChange(apt.id, value)}
                        >
                          <SelectTrigger className="w-36" data-testid={`status-select-${apt.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IN_ATTESA">In Attesa</SelectItem>
                            <SelectItem value="CONFERMATO">Confermato</SelectItem>
                            <SelectItem value="COMPLETATO">Completato</SelectItem>
                            <SelectItem value="ANNULLATO">Annullato</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <span className="font-bold text-primary">
                          {apt.service?.price ? formatPrice(apt.service.price) : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
